import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Swords, Trophy, Clock, Target, MessageSquare, Send, Shield, Crown, Medal, Flame, TrendingUp, X, Apple, Utensils, Zap, Dumbbell, Search, ArrowLeft } from 'lucide-react';
import HamburgerMenu from '../components/HamburgerMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const getBattleParticipants = (battle) => {
  if (Array.isArray(battle?.participants) && battle.participants.length > 0) {
    return battle.participants;
  }

  // Legacy duel compatibility.
  return [
    battle?.challenger_id
      ? {
          user_id: battle.challenger_id,
          username: battle.challenger_username,
          role: 'creator',
          invite_status: 'accepted',
          stats: battle.challenger_stats || {}
        }
      : null,
    battle?.opponent_id
      ? {
          user_id: battle.opponent_id,
          username: battle.opponent_username,
          role: 'participant',
          invite_status: 'accepted',
          stats: battle.opponent_stats || {}
        }
      : null
  ].filter(Boolean);
};

const getScoringParticipants = (battle) => {
  const participants = getBattleParticipants(battle);

  if (battle?.format !== 'group') {
    return participants;
  }

  const activeIds = Array.isArray(battle?.participant_ids)
    ? battle.participant_ids
    : [];

  return participants.filter((participant) => {
    if (participant.invite_status !== 'accepted') {
      return false;
    }

    return activeIds.length === 0 || activeIds.includes(participant.user_id);
  });
};

const getCurrentParticipant = (battle, userId) =>
  getBattleParticipants(battle).find(
    (participant) => participant.user_id === userId
  ) || null;

const getBattleLeaderboard = (battle) =>
  [...getScoringParticipants(battle)].sort(
    (a, b) =>
      (b.stats?.battle_points || 0) -
      (a.stats?.battle_points || 0)
  );

const BattleArena = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeBattles, setActiveBattles] = useState([]);
  const [pendingBattles, setPendingBattles] = useState([]);
  const [outgoingBattles, setOutgoingBattles] = useState([]);
  const [battleHistory, setBattleHistory] = useState([]);
  const [badges, setBadges] = useState([]);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [selectedBattle, setSelectedBattle] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  // Challenge form
  const [challengeMode, setChallengeMode] = useState('duel');
  const [challengeForm, setChallengeForm] = useState({
    opponent_identifier: '',
    battle_type: 'weekly',
    battle_category: 'workout',
    duration_days: 7,
    bet_amount: 0
  });
  const [opponentSearch, setOpponentSearch] = useState('');
  const [opponentResults, setOpponentResults] = useState([]);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [selectedOpponents, setSelectedOpponents] = useState([]);
  const [searchingOpponent, setSearchingOpponent] = useState(false);
  const searchTimeout = useRef(null);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        navigate('/');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Load all data with error handling for each endpoint
      const [userRes, activeRes, pendingRes, outgoingRes, historyRes, badgesRes] = await Promise.allSettled([
        axios.get(`${API}/user/profile`),
        axios.get(`${API}/battles/active`),
        axios.get(`${API}/battles/pending`),
        axios.get(`${API}/battles/outgoing`),
        axios.get(`${API}/battles/history`),
        axios.get(`${API}/user/badges`)
      ]);

      if (userRes.status === 'fulfilled') {
        setUser(userRes.value.data);
      }
      if (activeRes.status === 'fulfilled') {
        setActiveBattles(activeRes.value.data.active_battles || []);
      }
      if (pendingRes.status === 'fulfilled') {
        setPendingBattles(pendingRes.value.data.pending_battles || []);
      }
      if (outgoingRes.status === 'fulfilled') {
        setOutgoingBattles(outgoingRes.value.data.outgoing_battles || []);
      }
      if (historyRes.status === 'fulfilled') {
        setBattleHistory(historyRes.value.data.battle_history || []);
      }
      if (badgesRes.status === 'fulfilled') {
        setBadges(badgesRes.value.data.badges || []);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading battle data:', error);
      toast.error('Failed to load battle arena');
      setLoading(false);
    }
  };

  const loadActiveBattles = async () => {
    try {
      const response = await axios.get(`${API}/battles/active`);
      setActiveBattles(response.data.active_battles || []);
    } catch (error) {
      console.error('Error loading active battles:', error);
    }
  };

  const refreshBattleStats = async (battleId) => {
    try {
      const response = await axios.get(`${API}/battles/${battleId}/stats`);
      setSelectedBattle(response.data.battle);
    } catch (error) {
      console.error('Error refreshing battle stats:', error);
    }
  };

  useEffect(() => {
    loadData();
    
    // Seed a challenge when another app surface opens the Arena.
    if (
      location.state?.challengeMode === 'group' &&
      Array.isArray(location.state?.challengeMembers)
    ) {
      const seededMembers = location.state.challengeMembers
        .map((member) => ({
          id: member.user_id,
          username: member.username,
        }))
        .filter(
          (member) =>
            member.id &&
            member.username
        );

      setChallengeMode('group');
      setSelectedOpponents(seededMembers);
      setOpponentSearch('');
      setSelectedOpponent(null);
      setShowChallengeModal(true);

      // Clear navigation state so refresh/back does not reopen the composer.
      window.history.replaceState({}, document.title);
    } else if (location.state?.challengeUsername) {
      setChallengeMode('duel');

      setChallengeForm((prev) => ({
        ...prev,
        opponent_identifier:
          location.state.challengeUsername
      }));

      setOpponentSearch(
        location.state.challengeUsername
      );

      setSelectedOpponent({
        username: location.state.challengeUsername
      });

      setSelectedOpponents([]);
      setShowChallengeModal(true);

      // Clear navigation state so refresh/back does not reopen the composer.
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Polling effect for battle updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedBattle) {
        refreshBattleStats(selectedBattle.id);
      }
      loadActiveBattles();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [selectedBattle]);

  // WebSocket for real-time battle notifications
  const wsRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token || !WS_URL) return;

    const connectWS = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/ws/battles?token=${token}`);
        ws.onmessage = (event) => {
          const msg = JSON.parse(event.data);
          if (msg.type === 'battle_challenge') {
            toast.info(`New battle challenge from ${msg.data.challenger}!`);
            loadData();
          } else if (msg.type === 'battle_accepted') {
            toast.success('Your battle challenge was accepted!');
            loadData();
          } else if (msg.type === 'battle_message') {
            toast.info(`${msg.data.from}: ${msg.data.message}`);
            if (selectedBattle?.id === msg.data.battle_id) {
              loadBattleChat(msg.data.battle_id);
            }
          }
        };
        ws.onclose = () => {
          setTimeout(connectWS, 5000); // Reconnect after 5s
        };
        wsRef.current = ws;
      } catch (err) {
        console.error('WebSocket error:', err);
      }
    };

    connectWS();
    return () => wsRef.current?.close();
  }, [selectedBattle]);

  const resetChallengeComposer = () => {
    setChallengeMode('duel');
    setChallengeForm({
      opponent_identifier: '',
      battle_type: 'weekly',
      battle_category: 'workout',
      duration_days: 7,
      bet_amount: 0
    });
    setOpponentSearch('');
    setOpponentResults([]);
    setSelectedOpponent(null);
    setSelectedOpponents([]);
  };

  const sendChallenge = async () => {
    if (challengeMode === 'group') {
      if (selectedOpponents.length < 2) {
        toast.error('Select at least 2 opponents for a group Battle');
        return;
      }

      try {
        await axios.post(`${API}/battles/group`, {
          opponent_identifiers: selectedOpponents.map(
            (opponent) => opponent.username || opponent.email
          ),
          battle_type: challengeForm.battle_type,
          battle_category: challengeForm.battle_category,
          duration_days: challengeForm.duration_days
        });

        toast.success('Group Battle invitations sent!');
        setShowChallengeModal(false);
        resetChallengeComposer();
        loadData();
      } catch (error) {
        toast.error(
          error.response?.data?.detail ||
          'Failed to create group Battle'
        );
      }

      return;
    }

    if (!challengeForm.opponent_identifier.trim()) {
      toast.error('Please enter an opponent username or email');
      return;
    }

    try {
      await axios.post(`${API}/battles/challenge`, challengeForm);
      toast.success('Battle challenge sent!');
      setShowChallengeModal(false);
      resetChallengeComposer();
      loadData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'Failed to send challenge'
      );
    }
  };

  const acceptBattle = async (battleId) => {
    try {
      await axios.post(`${API}/battles/${battleId}/accept`);
      toast.success('Battle accepted! Let the war begin! ⚔️');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept battle');
    }
  };

  const declineBattle = async (battleId) => {
    try {
      await axios.post(`${API}/battles/${battleId}/decline`);
      toast.success('Battle declined');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline battle');
    }
  };

  const cancelBattle = async (battleId) => {
    try {
      await axios.post(`${API}/battles/${battleId}/cancel`);
      toast.success('Challenge withdrawn');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw challenge');
    }
  };

  const startGroupBattle = async (battleId) => {
    try {
      await axios.post(`${API}/battles/${battleId}/start`);
      toast.success('Group Battle started! ??');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start group Battle');
    }
  };

  const handleOpponentSearch = (query) => {
    setOpponentSearch(query);

    if (challengeMode === 'duel') {
      setSelectedOpponent(null);
      setChallengeForm((prev) => ({
        ...prev,
        opponent_identifier: ''
      }));
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (query.trim().length < 2) {
      setOpponentResults([]);
      setSearchingOpponent(false);
      return;
    }

    setSearchingOpponent(true);

    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/users/search`, {
          params: {
            query: query.trim(),
            limit: 8
          }
        });

        setOpponentResults(res.data.users || []);
      } catch {
        setOpponentResults([]);
      } finally {
        setSearchingOpponent(false);
      }
    }, 300);
  };

  const addGroupOpponent = (candidate) => {
    if (
      selectedOpponents.some(
        (opponent) => opponent.id === candidate.id
      )
    ) {
      return;
    }

    if (selectedOpponents.length >= 19) {
      toast.error('A group Battle can include up to 19 opponents');
      return;
    }

    setSelectedOpponents((current) => [
      ...current,
      candidate
    ]);

    setOpponentSearch('');
    setOpponentResults([]);
  };

  const removeGroupOpponent = (userId) => {
    setSelectedOpponents((current) =>
      current.filter(
        (opponent) => opponent.id !== userId
      )
    );
  };

  const selectOpponent = (candidate) => {
    if (challengeMode === 'group') {
      addGroupOpponent(candidate);
      return;
    }

    setSelectedOpponent(candidate);
    setOpponentSearch(candidate.username);
    setChallengeForm((prev) => ({
      ...prev,
      opponent_identifier: candidate.username
    }));
    setOpponentResults([]);
  };

  const switchChallengeMode = (mode) => {
    setChallengeMode(mode);
    setOpponentSearch('');
    setOpponentResults([]);
    setSelectedOpponent(null);
    setSelectedOpponents([]);

    setChallengeForm((current) => ({
      ...current,
      opponent_identifier: '',
      bet_amount: mode === 'group'
        ? 0
        : current.bet_amount
    }));
  };

  const loadBattleChat = async (battleId) => {
    try {
      const response = await axios.get(`${API}/battles/${battleId}/messages`);
      setChatMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading battle chat:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedBattle) return;
    
    try {
      await axios.post(`${API}/battles/${selectedBattle.id}/message`, null, {
        params: { message: newMessage }
      });
      setNewMessage('');
      loadBattleChat(selectedBattle.id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const viewBattle = async (battle) => {
    try {
      const response = await axios.get(`${API}/battles/${battle.id}/stats`);
      setSelectedBattle(response.data.battle);
      loadBattleChat(battle.id);
    } catch (error) {
      toast.error('Failed to load battle details');
    }
  };

  const getBattleTypeColor = (type) => {
    const colors = {
      'weekly': 'text-blood-400 border-blood-600',
      'monthly': 'text-purple-400 border-purple-600',
      'custom': 'text-amber-400 border-amber-600'
    };
    return colors[type] || 'text-stone-400 border-stone-600';
  };

  const getBattleTypeEmoji = (type) => {
    const emojis = {
      'weekly': '🥇',
      'monthly': '🥈',
      'custom': '⚔️'
    };
    return emojis[type] || '⚔️';
  };

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff < 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-blood-950">
        <HamburgerMenu />
        <div className="flex items-center justify-center pt-20">
          <div className="text-blood-400 text-xl">Loading Battle Arena...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-blood-950 text-white pb-20">
      <HamburgerMenu />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Back Button */}
        <button onClick={() => navigate('/hub')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-4 transition-colors" data-testid="back-to-hub-btn">
          <ArrowLeft className="w-4 h-4" />
          Back to Hub
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-blood-400 mb-2 tracking-wider flex items-center justify-center gap-2">
            <Swords className="w-8 h-8" />
            BATTLE ARENA
            <Swords className="w-8 h-8" />
          </h1>
          <p className="text-stone-300 text-sm">Challenge a friend • Track who wins</p>
        </div>

        {/* Badges Section */}
        {badges.length > 0 && (
          <Card className="glass border-blood-700 p-4 mb-4">
            <h2 className="text-lg font-bold text-blood-400 mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              YOUR TROPHIES
            </h2>
            <div className="flex gap-3 flex-wrap">
              {badges.map((badge) => (
                <div key={badge.id} className="glass border border-blood-700 rounded-lg p-3 flex items-center gap-2">
                  {badge.badge_type === 'gold_weekly' ? (
                    <Medal className="w-6 h-6 text-yellow-400" />
                  ) : (
                    <Crown className="w-6 h-6 text-purple-400" />
                  )}
                  <div>
                    <div className="text-sm font-bold text-stone-200">
                      {badge.badge_type === 'gold_weekly' ? '🥇 Weekly Champion' : '🥈 Monthly Champion'}
                    </div>
                    <div className="text-xs text-stone-300">{badge.wins_count} Wins</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Challenge Button */}
        <div className="flex justify-center mb-4">
          <Button
            onClick={() => setShowChallengeModal(true)}
            className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-3 px-6 text-base tracking-wider"
            data-testid="send-challenge-btn"
          >
            <Swords className="w-5 h-5 mr-2" />
            SEND BATTLE CHALLENGE
          </Button>
        </div>

        {/* Empty State - No Battles */}
        {activeBattles.length === 0 && pendingBattles.length === 0 && outgoingBattles.length === 0 && battleHistory.length === 0 && (
          <Card className="glass border-stone-700 p-6 mb-4 text-center" data-testid="empty-state">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-blood-900/30 flex items-center justify-center mb-4">
                <Swords className="w-10 h-10 text-blood-500" />
              </div>
              <h3 className="text-xl font-bold text-blood-400 mb-2">No Active Battles</h3>
              <p className="text-stone-300 mb-4 text-sm max-w-sm">
                Challenge a friend and track who wins. Compete on workouts, streaks, and consistency.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-lg">
                <div className="glass border border-blood-800/50 rounded-lg p-3 text-left">
                  <div className="flex items-center gap-2 text-blood-400 font-bold mb-1 text-sm">
                    <Target className="w-4 h-4" />
                    WORKOUT
                  </div>
                  <p className="text-xs text-stone-300">Compete on workouts & streaks</p>
                </div>
                <div className="glass border border-amber-800/50 rounded-lg p-3 text-left">
                  <div className="flex items-center gap-2 text-amber-400 font-bold mb-1 text-sm">
                    <Flame className="w-4 h-4" />
                    NUTRITION
                  </div>
                  <p className="text-xs text-stone-300">Battle on calorie goals</p>
                </div>
                <div className="glass border border-purple-800/50 rounded-lg p-3 text-left">
                  <div className="flex items-center gap-2 text-purple-400 font-bold mb-1 text-sm">
                    <Crown className="w-4 h-4" />
                    COMBINED
                  </div>
                  <p className="text-xs text-stone-300">Ultimate showdown</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Pending Battles */}
        {pendingBattles.length > 0 && (
          <Card className="glass border-amber-700 p-4 mb-4">
            <h2 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              INCOMING ({pendingBattles.length})
            </h2>
            <div className="space-y-2">
              {pendingBattles.map((battle) => {
                const participants = getBattleParticipants(battle);
                const creator = participants.find(
                  (participant) => participant.role === 'creator'
                );
                const isGroup = battle.format === 'group';

                return (
                  <div key={battle.id} className="glass border border-amber-700 rounded-lg p-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="font-bold text-amber-400">
                          {getBattleTypeEmoji(battle.battle_type)}{' '}
                          {isGroup
                            ? `${creator?.username || battle.challenger_username} invited you to a group Battle`
                            : `${battle.challenger_username} challenges you!`}
                        </div>
                        <div className="text-xs text-stone-300">
                          {battle.battle_type.toUpperCase()} ? {battle.battle_category.toUpperCase()} ? {battle.duration_days} days
                        </div>
                        {isGroup && (
                          <div className="text-xs text-stone-400 mt-1">
                            {participants.length} invited participants
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => acceptBattle(battle.id)}
                          className="bg-blood-700 hover:bg-blood-800 text-sm"
                          size="sm"
                        >
                          Accept ??
                        </Button>
                        <Button
                          onClick={() => declineBattle(battle.id)}
                          variant="outline"
                          className="border-stone-700 text-stone-400 text-sm"
                          size="sm"
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Outgoing Challenges */}
        {outgoingBattles.length > 0 && (
          <Card className="glass border-amber-700 p-4 mb-4" data-testid="outgoing-battles">
            <h2 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              OUTGOING CHALLENGES ({outgoingBattles.length})
            </h2>
            <div className="space-y-2">
              {outgoingBattles.map((battle) => {
                const isGroup = battle.format === 'group';
                const participants = getBattleParticipants(battle);
                const acceptedCount = participants.filter(
                  (participant) => participant.invite_status === 'accepted'
                ).length;
                const invitedCount = participants.filter(
                  (participant) => participant.invite_status === 'invited'
                ).length;
                const declinedCount = participants.filter(
                  (participant) => participant.invite_status === 'declined'
                ).length;

                return (
                  <div
                    key={battle.id}
                    className="glass border border-amber-800/50 rounded-lg p-3"
                    data-testid={`outgoing-battle-${battle.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <span className="text-stone-200 font-bold">
                          {isGroup
                            ? `Group Battle ? ${participants.length} participants`
                            : battle.opponent_username}
                        </span>
                        <div className="text-xs text-amber-400 mt-0.5">
                          {battle.battle_type.toUpperCase()} - {battle.battle_category.toUpperCase()} - {battle.duration_days} days
                        </div>

                        {isGroup ? (
                          <div className="text-xs text-stone-400 mt-1">
                            {acceptedCount} accepted ? {invitedCount} waiting
                            {declinedCount > 0 ? ` ? ${declinedCount} declined` : ''}
                          </div>
                        ) : (
                          <div className="text-xs text-stone-500 mt-0.5">
                            {battle.status === 'pending' ? 'Waiting for response...' : 'In progress'}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        {isGroup && battle.status === 'pending' && acceptedCount >= 2 && (
                          <Button
                            onClick={() => startGroupBattle(battle.id)}
                            className="bg-blood-700 hover:bg-blood-800 text-sm"
                            size="sm"
                          >
                            Start Battle
                          </Button>
                        )}

                        <Button
                          onClick={() => cancelBattle(battle.id)}
                          variant="outline"
                          className="border-stone-700 text-stone-400 text-sm"
                          size="sm"
                          data-testid={`cancel-battle-${battle.id}`}
                        >
                          Withdraw
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Active Battles */}
        {activeBattles.length > 0 && (
          <Card className="glass border-blood-700 p-4 mb-4">
            <h2 className="text-lg font-bold text-blood-400 mb-3 flex items-center gap-2">
              <Flame className="w-5 h-5" />
              ACTIVE BATTLES ({activeBattles.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activeBattles.map((battle) => {
                if (battle.format === 'group') {
                  const leaderboard = getBattleLeaderboard(battle);
                  const currentParticipant = getCurrentParticipant(
                    battle,
                    user?.id
                  );
                  const myRank = leaderboard.findIndex(
                    (participant) => participant.user_id === user?.id
                  );

                  return (
                    <div
                      key={battle.id}
                      onClick={() => viewBattle(battle)}
                      className="glass border border-blood-700 rounded-lg p-3 cursor-pointer hover:border-blood-500 transition-all active:scale-[0.98]"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`font-bold text-sm ${getBattleTypeColor(battle.battle_type)}`}>
                          {getBattleTypeEmoji(battle.battle_type)} GROUP {battle.battle_type.toUpperCase()}
                        </div>
                        <div className="text-xs text-stone-300 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {getTimeRemaining(battle.end_date)}
                        </div>
                      </div>

                      <div className="text-center mb-3">
                        <div className="text-lg font-bold text-white">
                          {leaderboard.length} Warriors
                        </div>
                        <div className="text-xs text-stone-300">
                          {battle.battle_category.toUpperCase()}
                        </div>
                      </div>

                      <div className="glass border border-blood-800 rounded p-2 mb-2 text-center">
                        <div className="text-xl font-bold text-blood-400">
                          {currentParticipant?.stats?.battle_points || 0}
                        </div>
                        <div className="text-xs text-stone-300">
                          You{myRank >= 0 ? ` ? Rank ${myRank + 1}` : ''}
                        </div>
                      </div>

                      <div className="space-y-1">
                        {leaderboard.slice(0, 3).map((participant, index) => (
                          <div
                            key={participant.user_id}
                            className="flex items-center justify-between text-xs text-stone-300"
                          >
                            <span>
                              #{index + 1} {participant.username}
                              {participant.user_id === user?.id ? ' (You)' : ''}
                            </span>
                            <span className="font-bold text-white">
                              {participant.stats?.battle_points || 0} pts
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                const isChallenger = battle.challenger_id === user?.id;
                const myStats = isChallenger ? battle.challenger_stats : battle.opponent_stats;
                const opponentStats = isChallenger ? battle.opponent_stats : battle.challenger_stats;
                const opponentName = isChallenger ? battle.opponent_username : battle.challenger_username;

                return (
                  <div
                    key={battle.id}
                    onClick={() => viewBattle(battle)}
                    className="glass border border-blood-700 rounded-lg p-3 cursor-pointer hover:border-blood-500 transition-all active:scale-[0.98]"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`font-bold text-sm ${getBattleTypeColor(battle.battle_type)}`}>
                        {getBattleTypeEmoji(battle.battle_type)} {battle.battle_type.toUpperCase()}
                      </div>
                      <div className="text-xs text-stone-300 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getTimeRemaining(battle.end_date)}
                      </div>
                    </div>

                    <div className="text-center mb-2">
                      <div className="text-lg font-bold text-white">VS {opponentName}</div>
                      <div className="text-xs text-stone-300">{battle.battle_category.toUpperCase()}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="glass border border-blood-800 rounded p-2">
                        <div className="text-xl font-bold text-blood-400">{myStats?.battle_points || 0}</div>
                        <div className="text-xs text-stone-300">You</div>
                      </div>
                      <div className="glass border border-stone-700 rounded p-2">
                        <div className="text-xl font-bold text-stone-300">{opponentStats?.battle_points || 0}</div>
                        <div className="text-xs text-stone-300">Opponent</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Battle History Preview */}
        {battleHistory.length > 0 && (
          <Card className="glass border-stone-700 p-4">
            <h2 className="text-lg font-bold text-stone-300 mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              BATTLE HISTORY
            </h2>
            <div className="space-y-2">
              {battleHistory.slice(0, 5).map((battle) => {
                const winnerIds = Array.isArray(battle.winner_ids)
                  ? battle.winner_ids
                  : battle.winner_id
                    ? [battle.winner_id]
                    : [];
                const won = winnerIds.includes(user?.id);
                const hasWinner = winnerIds.length > 0;

                if (battle.format === 'group') {
                  const participants = getBattleParticipants(battle);
                  const winners = participants.filter(
                    (participant) => winnerIds.includes(participant.user_id)
                  );

                  return (
                    <div key={battle.id} className="glass border border-stone-800 rounded-lg p-2 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={won ? 'text-blood-400 font-bold' : 'text-stone-300'}>
                            {won ? '?? WIN' : hasWinner ? '?? LOSS' : '?? TIE'}
                          </span>
                          <span className="text-stone-300 text-sm">
                            Group Battle ? {participants.length} participants
                          </span>
                        </div>
                        {winners.length > 0 && (
                          <div className="text-xs text-stone-500 mt-1">
                            Winner{winners.length > 1 ? 's' : ''}: {winners.map((participant) => participant.username).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-stone-300">
                        {new Date(battle.end_date).toLocaleDateString()}
                      </div>
                    </div>
                  );
                }

                const isChallenger = battle.challenger_id === user?.id;
                const opponentName = isChallenger ? battle.opponent_username : battle.challenger_username;

                return (
                  <div key={battle.id} className="glass border border-stone-800 rounded-lg p-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={won ? 'text-blood-400 font-bold' : 'text-stone-300'}>
                        {won ? '?? WIN' : hasWinner ? '?? LOSS' : '?? TIE'}
                      </span>
                      <span className="text-stone-300 text-sm">vs {opponentName}</span>
                    </div>
                    <div className="text-xs text-stone-300">
                      {new Date(battle.end_date).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Challenge Modal - Fixed for mobile */}
      {showChallengeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <Card className="glass border-blood-700 p-4 sm:p-6 w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl rounded-b-none sm:rounded-b-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blood-400 flex items-center gap-2">
                <Swords className="w-5 h-5" />
                {challengeMode === 'group'
                  ? 'CREATE GROUP BATTLE'
                  : 'SEND CHALLENGE'}
              </h2>

              <button
                onClick={() => {
                  setShowChallengeModal(false);
                  resetChallengeComposer();
                }}
                className="text-stone-400 hover:text-white p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-stone-300 text-sm mb-2 block">
                  Battle Format
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    onClick={() => switchChallengeMode('duel')}
                    variant={
                      challengeMode === 'duel'
                        ? 'default'
                        : 'outline'
                    }
                    className={
                      challengeMode === 'duel'
                        ? 'bg-blood-700 hover:bg-blood-800'
                        : 'border-stone-700 text-stone-300'
                    }
                  >
                    1 vs 1
                  </Button>

                  <Button
                    type="button"
                    onClick={() => switchChallengeMode('group')}
                    variant={
                      challengeMode === 'group'
                        ? 'default'
                        : 'outline'
                    }
                    className={
                      challengeMode === 'group'
                        ? 'bg-blood-700 hover:bg-blood-800'
                        : 'border-stone-700 text-stone-300'
                    }
                  >
                    Group Battle
                  </Button>
                </div>
              </div>

              {challengeMode === 'group' && (
                <div>
                  <label className="text-stone-300 text-sm mb-2 block">
                    Selected Opponents ({selectedOpponents.length})
                  </label>

                  {selectedOpponents.length === 0 ? (
                    <div className="text-xs text-stone-500">
                      Select at least 2 opponents. A group Battle can contain up to 20 total participants.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedOpponents.map((opponent) => (
                        <div
                          key={opponent.id}
                          className="glass border border-blood-800 rounded-lg px-3 py-2 flex items-center gap-2"
                        >
                          <span className="text-sm text-white font-bold">
                            {opponent.username}
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              removeGroupOpponent(opponent.id)
                            }
                            className="text-stone-500 hover:text-blood-400"
                            aria-label={`Remove ${opponent.username}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="relative">
                <label className="text-stone-300 text-sm mb-2 block">
                  {challengeMode === 'group'
                    ? 'Add Opponents'
                    : 'Find Opponent'}
                </label>

                {challengeMode === 'duel' && selectedOpponent ? (
                  <div
                    className="flex items-center justify-between glass border border-blood-700 px-4 py-3 rounded-lg"
                    data-testid="selected-opponent"
                  >
                    <div>
                      <span className="text-white font-bold">
                        {selectedOpponent.username}
                      </span>

                      {selectedOpponent.email && (
                        <span className="text-stone-500 text-sm ml-2">
                          {selectedOpponent.email}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        setSelectedOpponent(null);
                        setOpponentSearch('');
                        setChallengeForm((prev) => ({
                          ...prev,
                          opponent_identifier: ''
                        }));
                      }}
                      className="text-stone-400 hover:text-blood-400"
                      data-testid="clear-opponent"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />

                      <input
                        type="text"
                        value={opponentSearch}
                        onChange={(e) =>
                          handleOpponentSearch(e.target.value)
                        }
                        className="w-full glass border border-stone-700 text-white pl-10 pr-4 py-3 rounded-lg"
                        placeholder={
                          challengeMode === 'group'
                            ? 'Search and add opponents...'
                            : 'Search by username or email...'
                        }
                        autoFocus
                        data-testid="opponent-search-input"
                      />

                      {searchingOpponent && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs">
                          searching...
                        </div>
                      )}
                    </div>

                    {opponentResults.length > 0 && (
                      <div
                        className="absolute left-0 right-0 mt-1 bg-stone-900 border border-stone-700 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto"
                        data-testid="opponent-search-results"
                      >
                        {opponentResults
                          .filter((candidate) => {
                            if (candidate.id === user?.id) {
                              return false;
                            }

                            if (challengeMode !== 'group') {
                              return true;
                            }

                            return !selectedOpponents.some(
                              (opponent) =>
                                opponent.id === candidate.id
                            );
                          })
                          .map((candidate) => (
                            <button
                              key={candidate.id}
                              onClick={() =>
                                selectOpponent(candidate)
                              }
                              className="w-full px-4 py-3 text-left hover:bg-stone-800 transition-colors flex items-center justify-between border-b border-stone-800 last:border-0"
                              data-testid={`opponent-result-${candidate.username}`}
                            >
                              <div>
                                <span className="text-white font-bold">
                                  {candidate.username}
                                </span>

                                {candidate.skill_level && (
                                  <span className="text-stone-500 text-xs ml-2">
                                    {candidate.skill_level}
                                  </span>
                                )}
                              </div>

                              {candidate.fitness_goal && (
                                <span className="text-stone-600 text-xs">
                                  {candidate.fitness_goal}
                                </span>
                              )}
                            </button>
                          ))}
                      </div>
                    )}

                    {opponentSearch.length >= 2 &&
                      !searchingOpponent &&
                      opponentResults.length === 0 && (
                        <div className="absolute left-0 right-0 mt-1 bg-stone-900 border border-stone-700 rounded-lg px-4 py-3 text-stone-500 text-sm z-10">
                          No users found for "{opponentSearch}"
                        </div>
                      )}
                  </>
                )}
              </div>

              <div>
                <label className="text-stone-300 text-sm mb-2 block">
                  Battle Type
                </label>

                <select
                  value={challengeForm.battle_type}
                  onChange={(e) =>
                    setChallengeForm({
                      ...challengeForm,
                      battle_type: e.target.value
                    })
                  }
                  className="w-full glass border border-stone-700 text-white px-4 py-3 rounded-lg"
                >
                  <option value="weekly">Weekly (7 days)</option>
                  <option value="monthly">Monthly (30 days)</option>

                  {user?.tier === 'premium' && (
                    <option value="custom">
                      Custom Duration
                    </option>
                  )}
                </select>
              </div>

              <div>
                <label className="text-stone-300 text-sm mb-2 block">
                  Battle Category
                </label>

                <select
                  value={challengeForm.battle_category}
                  onChange={(e) =>
                    setChallengeForm({
                      ...challengeForm,
                      battle_category: e.target.value
                    })
                  }
                  className="w-full glass border border-stone-700 text-white px-4 py-3 rounded-lg"
                >
                  <option value="workout">Workout Only</option>
                  <option value="nutrition">Nutrition Only</option>
                  <option value="combined">Combined</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={sendChallenge}
                disabled={
                  challengeMode === 'group'
                    ? selectedOpponents.length < 2
                    : !challengeForm.opponent_identifier
                }
                className="flex-1 bg-blood-700 hover:bg-blood-800 py-3"
              >
                {challengeMode === 'group'
                  ? `Invite ${selectedOpponents.length} Opponents`
                  : 'Send Challenge'}
              </Button>

              <Button
                onClick={() => {
                  setShowChallengeModal(false);
                  resetChallengeComposer();
                }}
                variant="outline"
                className="flex-1 border-stone-700 py-3"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Battle Detail Modal - Fixed for mobile */}
      {selectedBattle && (
        <div className="fixed inset-0 bg-black/90 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <Card className="glass border-blood-700 p-4 sm:p-6 w-full sm:max-w-2xl sm:rounded-lg rounded-t-2xl rounded-b-none sm:rounded-b-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blood-400">
                {getBattleTypeEmoji(selectedBattle.battle_type)} BATTLE DETAILS
              </h2>
              <button
                onClick={() => setSelectedBattle(null)}
                className="text-stone-400 hover:text-white p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {selectedBattle.format === 'group' ? (
              <div className="space-y-2 mb-4">
                {getBattleLeaderboard(selectedBattle).map((participant, index) => (
                  <div
                    key={participant.user_id}
                    className="glass border border-blood-800 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-stone-200 text-sm">
                          #{index + 1} {participant.username}
                          {participant.user_id === user?.id && ' (You)'}
                        </h3>
                        <div className="text-xs text-stone-500 mt-1">
                          {participant.role === 'creator' ? 'Battle creator' : 'Participant'}
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-blood-400">
                        {participant.stats?.battle_points || 0}
                      </div>
                    </div>

                    {(selectedBattle.battle_category === 'workout' || selectedBattle.battle_category === 'combined') && (
                      <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-stone-300">
                        <div>
                          <Dumbbell className="w-3 h-3 inline mr-1" />
                          {participant.stats?.workouts_completed || 0} workouts
                        </div>
                        <div>
                          <Flame className="w-3 h-3 inline mr-1" />
                          {participant.stats?.current_streak || 0} streak
                        </div>
                      </div>
                    )}

                    {(selectedBattle.battle_category === 'nutrition' || selectedBattle.battle_category === 'combined') && (
                      <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-stone-300">
                        <div>
                          <Utensils className="w-3 h-3 inline mr-1" />
                          {participant.stats?.days_logged_nutrition || 0} days logged
                        </div>
                        <div>
                          <Target className="w-3 h-3 inline mr-1" />
                          {participant.stats?.days_hit_calorie_goal || 0} calorie goals hit
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Challenger Stats */}
                <div className="glass border border-blood-700 rounded-lg p-3">
                  <h3 className="font-bold text-blood-400 mb-2 text-sm">
                    {selectedBattle.challenger_username}
                    {selectedBattle.challenger_id === user?.id && ' (You)'}
                  </h3>
                  <div className="text-2xl font-bold text-blood-400 mb-1">
                    {selectedBattle.challenger_stats?.battle_points || 0}
                  </div>
                  {(selectedBattle.battle_category === 'workout' || selectedBattle.battle_category === 'combined') && (
                    <>
                      <div className="text-xs text-stone-300">
                        <Dumbbell className="w-3 h-3 inline mr-1" />{selectedBattle.challenger_stats?.workouts_completed || 0} workouts
                      </div>
                      <div className="text-xs text-stone-300">
                        <Flame className="w-3 h-3 inline mr-1" />{selectedBattle.challenger_stats?.current_streak || 0} streak
                      </div>
                    </>
                  )}
                  {(selectedBattle.battle_category === 'nutrition' || selectedBattle.battle_category === 'combined') && (
                    <>
                      <div className="text-xs text-stone-300">
                        <Utensils className="w-3 h-3 inline mr-1" />{selectedBattle.challenger_stats?.days_logged_nutrition || 0} days logged
                      </div>
                      <div className="text-xs text-stone-300">
                        <Target className="w-3 h-3 inline mr-1" />{selectedBattle.challenger_stats?.days_hit_calorie_goal || 0} calorie goals hit
                      </div>
                      <div className="text-xs text-stone-300">
                        <Zap className="w-3 h-3 inline mr-1" />{Math.round(selectedBattle.challenger_stats?.protein_grams || 0)}g protein
                      </div>
                    </>
                  )}
                </div>

                {/* Opponent Stats */}
                <div className="glass border border-stone-700 rounded-lg p-3">
                  <h3 className="font-bold text-stone-300 mb-2 text-sm">
                    {selectedBattle.opponent_username}
                    {selectedBattle.opponent_id === user?.id && ' (You)'}
                  </h3>
                  <div className="text-2xl font-bold text-stone-300 mb-1">
                    {selectedBattle.opponent_stats?.battle_points || 0}
                  </div>
                  {(selectedBattle.battle_category === 'workout' || selectedBattle.battle_category === 'combined') && (
                    <>
                      <div className="text-xs text-stone-300">
                        <Dumbbell className="w-3 h-3 inline mr-1" />{selectedBattle.opponent_stats?.workouts_completed || 0} workouts
                      </div>
                      <div className="text-xs text-stone-300">
                        <Flame className="w-3 h-3 inline mr-1" />{selectedBattle.opponent_stats?.current_streak || 0} streak
                      </div>
                    </>
                  )}
                  {(selectedBattle.battle_category === 'nutrition' || selectedBattle.battle_category === 'combined') && (
                    <>
                      <div className="text-xs text-stone-300">
                        <Utensils className="w-3 h-3 inline mr-1" />{selectedBattle.opponent_stats?.days_logged_nutrition || 0} days logged
                      </div>
                      <div className="text-xs text-stone-300">
                        <Target className="w-3 h-3 inline mr-1" />{selectedBattle.opponent_stats?.days_hit_calorie_goal || 0} calorie goals hit
                      </div>
                      <div className="text-xs text-stone-300">
                        <Zap className="w-3 h-3 inline mr-1" />{Math.round(selectedBattle.opponent_stats?.protein_grams || 0)}g protein
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Battle Info */}
            <div className="glass border border-stone-800 rounded-lg p-3 mb-4">
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <div className="text-stone-300 text-xs">Type</div>
                  <div className="font-bold text-white">{selectedBattle.battle_type.toUpperCase()}</div>
                </div>
                <div>
                  <div className="text-stone-300 text-xs">Category</div>
                  <div className="font-bold text-white">{selectedBattle.battle_category.toUpperCase()}</div>
                </div>
                <div>
                  <div className="text-stone-300 text-xs">Time Left</div>
                  <div className="font-bold text-white">{getTimeRemaining(selectedBattle.end_date)}</div>
                </div>
              </div>
            </div>

            {/* Trash Talk Chat */}
            <div className="glass border border-blood-800 rounded-lg p-3">
              <h3 className="font-bold text-blood-400 mb-2 flex items-center gap-2 text-sm">
                <MessageSquare className="w-4 h-4" />
                TRASH TALK
              </h3>
              
              <div className="h-40 overflow-y-auto mb-2 space-y-2">
                {chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-2 rounded text-sm ${
                      msg.user_id === user?.id 
                        ? 'bg-blood-950/50 border border-blood-800 ml-6' 
                        : 'bg-stone-950/50 border border-stone-800 mr-6'
                    }`}
                  >
                    <div className="text-xs text-stone-300 mb-1">{msg.username}</div>
                    <div className="text-white">{msg.message}</div>
                  </div>
                ))}
                {chatMessages.length === 0 && (
                  <div className="text-center text-stone-300 py-6 text-sm">
                    No messages yet. Start the trash talk! 💬
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1 glass border border-stone-700 text-white px-3 py-2 rounded text-sm"
                  placeholder="Send a message..."
                  maxLength={200}
                />
                <Button
                  onClick={sendMessage}
                  className="bg-blood-700 hover:bg-blood-800"
                  size="sm"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Button
              onClick={() => refreshBattleStats(selectedBattle.id)}
              className="w-full mt-3 bg-stone-800 hover:bg-stone-700"
            >
              🔄 Refresh Stats
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BattleArena;
